import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { candidateApi } from "@/lib/candidateApi";
import { HomeShell } from "@/shell/HomeShell";
import { Spinner } from "@/components/primitives";
import type { BriefData } from "@/lib/types";
import { formatDate } from "@/lib/format";
import { initialsFromName } from "@/lib/initials";

// One role's brief, opened from a "Your Briefs" row. The :token in the URL is
// the role being read, which is often NOT the invite the candidate signed in on
// — see api_invite_brief for why that read is allowed.

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="rounded-[18px] border px-5 py-4"
      style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
    >
      <p
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] mb-2.5"
        style={{ color: "#7C7590" }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

function Prose({ text }: { text: string }) {
  return (
    <p
      className="text-[14px] leading-relaxed whitespace-pre-wrap"
      style={{ color: "#4A4260" }}
    >
      {text}
    </p>
  );
}

export function Brief() {
  const { token = "" } = useParams<{ token: string }>();
  const [data, setData] = useState<BriefData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    candidateApi.getBrief(token).then((r) => {
      setLoading(false);
      if (r.ok) setData(r as BriefData);
      else setError((r as { error?: string }).error ?? "Could not load this brief.");
    });
  }, [token]);

  if (loading) {
    return (
      <HomeShell activeTab="briefs">
        <div className="flex justify-center py-20">
          <Spinner size={32} />
        </div>
      </HomeShell>
    );
  }
  if (error || !data) {
    return (
      <HomeShell activeTab="briefs">
        <div className="py-20 text-center text-slate-500">{error ?? "Something went wrong."}</div>
      </HomeShell>
    );
  }

  // An invite can carry a JD as extracted text, as a link, or as neither.
  const hasAnything = data.jdText || data.jdUrl || data.companyBrief || data.prepNotes;

  return (
    <HomeShell
      agencyName={data.agencyName}
      logoUrl={data.branding?.logoUrlLight || undefined}
      logoFilter={data.branding?.logoFilter}
      candidateInitials={initialsFromName(data.displayName)}
      activeTab="briefs"
    >
      <div className="max-w-[640px]">
        <Link
          to={`/invite/${token}/briefs`}
          className="text-[12.5px] font-semibold"
          style={{ color: "var(--accent)" }}
        >
          ← All briefs
        </Link>

        <div className="mt-4 mb-6">
          <p
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: "#7C7590" }}
          >
            Brief
          </p>
          <h1 className="text-[22px] font-semibold mt-1" style={{ color: "#17131F" }}>
            {data.role}
          </h1>
          <p className="text-[13px] mt-1" style={{ color: "#7C7590" }}>
            {data.company}
            {data.createdAt ? ` · sent ${formatDate(data.createdAt)}` : ""}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {!hasAnything && (
            <Section label="Job description">
              <p className="text-[13px] italic" style={{ color: "#8C86A0" }}>
                Your consultant has not attached a job description to this role yet.
              </p>
            </Section>
          )}

          {data.jdText && (
            <Section label="Job description">
              <Prose text={data.jdText} />
            </Section>
          )}

          {data.jdUrl && (
            // Labelled as the posting, never as the JD itself: the link may not
            // have been readable, so the bare URL is not a job description (QA F20).
            <Section label="Original posting">
              {/* Server-side allowlisted to http(s). noreferrer as well as
                  noopener: the target is a third-party page the recruiter chose,
                  and it has no business reading the candidate portal's URL. */}
              <a
                href={data.jdUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[14px] font-semibold break-all"
                style={{ color: "var(--accent)" }}
              >
                Open the job posting ↗
              </a>
            </Section>
          )}

          {data.companyBrief && (
            <Section label="Company research">
              {/* Markdown rendered and sanitised server-side, as the scorecard is. */}
              <div className="prose-scorecard" dangerouslySetInnerHTML={{ __html: data.companyBriefHtml }} />
            </Section>
          )}

          {data.prepNotes && (
            <Section label="Your prep notes">
              <Prose text={data.prepNotes} />
            </Section>
          )}
        </div>
      </div>
    </HomeShell>
  );
}
